import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import {
  approveProcurementOrder,
  closeProcurementOrder,
  getProcurementOrder,
  listSuppliers,
  markProcurementOrderOrdered,
  receiveProcurementOrder,
  receiveProcurementOrderPartial,
  rejectProcurementOrder,
  reviewProcurementOrder,
  linkProcurementOrderTarget,
  updateProcurementOrder,
} from "../services/procurementService";
import ProcurementOrderStatusBadge from "../components/procurement/ProcurementOrderStatusBadge";
import ProcurementOrderAssociationBadge from "../components/procurement/ProcurementOrderAssociationBadge";
import ProcurementOrderLinesTable from "../components/procurement/ProcurementOrderLinesTable";
import ProcurementOrderForm from "../components/procurement/ProcurementOrderForm";
import ProcurementOrderProcessingActions from "../components/procurement/ProcurementOrderProcessingActions";
import ProcurementOrderReviewPanel from "../components/procurement/ProcurementOrderReviewPanel";
import ProcurementOrderReceivePanel from "../components/procurement/ProcurementOrderReceivePanel";
import procurementErrorMessage from "../components/procurement/procurementErrorMessage";

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("nl-NL");
}

export default function ProcurementOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [editing, setEditing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [linkTargetType, setLinkTargetType] = useState("project");
  const [linkTargetId, setLinkTargetId] = useState("");
  const [linkNote, setLinkNote] = useState("");
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [orderRes, suppliersRes, clientsRes, projectsRes, workOrdersRes] = await Promise.all([
        getProcurementOrder(id),
        listSuppliers({ includeInactive: true }),
        api.get("/clients"),
        api.get("/projects"),
        api.get("/werkbonnen"),
      ]);
      setOrder(orderRes || null);
      setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : []);
      setClients(Array.isArray(clientsRes?.data) ? clientsRes.data : []);
      setProjects(Array.isArray(projectsRes?.data) ? projectsRes.data : []);
      setWorkOrders(Array.isArray(workOrdersRes?.data) ? workOrdersRes.data : []);
    } catch (err) {
      setError(procurementErrorMessage(err, "Order laden mislukt."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const runNamedAction = async (actionKey, note = "") => {
    try {
      setActionBusy(true);
      setError("");
      if (actionKey === "review") await reviewProcurementOrder(id, { note });
      if (actionKey === "approve") await approveProcurementOrder(id, { note });
      if (actionKey === "reject") await rejectProcurementOrder(id, { note });
      if (actionKey === "order") await markProcurementOrderOrdered(id, { note });
      if (actionKey === "receive_partial") await receiveProcurementOrderPartial(id, { note });
      if (actionKey === "receive") await receiveProcurementOrder(id, { note });
      if (actionKey === "close") await closeProcurementOrder(id, { note });
      await load();
    } catch (err) {
      setError(procurementErrorMessage(err, "Statusactie mislukt."));
    } finally {
      setActionBusy(false);
    }
  };

  const saveEdit = async (payload) => {
    setActionBusy(true);
    try {
      await updateProcurementOrder(id, payload);
      setEditing(false);
      await load();
    } finally {
      setActionBusy(false);
    }
  };

  const handleLinkTarget = async () => {
    if (!String(linkTargetId || "").trim()) {
      setError("Kies een doel om te koppelen.");
      return;
    }
    try {
      setLinking(true);
      setError("");
      await linkProcurementOrderTarget(id, {
        targetType: linkTargetType,
        targetId: linkTargetId,
        note: linkNote,
      });
      setLinkNote("");
      await load();
    } catch (err) {
      setError(procurementErrorMessage(err, "Later koppelen mislukt."));
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">Order laden...</div>;
  }

  if (error && !order) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        <button type="button" onClick={() => navigate("/pl/inkoop")} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
          Terug naar inkooporders
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Inkooporder</p>
            <h1 className="text-2xl font-semibold text-slate-900">{order?.orderNumber || "-"}</h1>
            <p className="text-sm text-slate-600">{order?.title || "-"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/pl/inkoop" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">Terug</Link>
            <button type="button" onClick={() => setEditing((prev) => !prev)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              {editing ? "Bewerken sluiten" : "Bewerken"}
            </button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

      {editing && order && (
        <ProcurementOrderForm
          initialValue={order}
          suppliers={suppliers}
          clients={clients}
          projects={projects}
          workOrders={workOrders}
          onCancel={() => setEditing(false)}
          onSubmit={saveEdit}
          submitting={actionBusy}
        />
      )}

      {order && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 text-sm text-slate-700">
              <p><span className="text-slate-500">Type:</span> {order.type}</p>
              <p><span className="text-slate-500">Hoort bij:</span> <ProcurementOrderAssociationBadge associationType={order.associationType} /></p>
              <p>
                <span className="text-slate-500">Status:</span>{" "}
                <ProcurementOrderStatusBadge
                  status={order.status}
                  displayStatus={order.displayStatus || order.displayStatusKey}
                  label={order.displayStatusLabel}
                />
              </p>
              <p><span className="text-slate-500">Volgende stap:</span> {order.nextActionLabel || "-"}</p>
              <p><span className="text-slate-500">Leverancier:</span> {order.supplierName || "-"}</p>
              <p><span className="text-slate-500">Resource:</span> {order.resourceType || "-"} {order.resourceName ? `· ${order.resourceName}` : ""}</p>
              <p><span className="text-slate-500">Gemaakt op:</span> {formatDateTime(order.createdAt)}</p>
              <p><span className="text-slate-500">Aangevraagd door:</span> {order.createdBy?.name || order.createdBy?.email || "-"}</p>
            </div>
            <div className="space-y-1 text-sm text-slate-700">
              <p><span className="text-slate-500">Klant:</span> {order.clientName || "-"}</p>
              <p><span className="text-slate-500">Project:</span> {order.projectName || "-"}</p>
              <p><span className="text-slate-500">Werkbon:</span> {order.workOrderTitle || "-"}</p>
              <p><span className="text-slate-500">Geplande periode:</span> {formatDateTime(order.plannedStart)} - {formatDateTime(order.plannedEnd)}</p>
              <p><span className="text-slate-500">Toegewezen aan:</span> {order.assignedTo?.name || order.assignedTo?.email || "-"}</p>
            </div>
          </div>

          {order.type === "service_order" && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <p><span className="text-slate-500">Prijsmodel:</span> {order.pricingModel || "-"}</p>
              <p><span className="text-slate-500">Tarief:</span> {order.rate ?? "-"} </p>
              <p><span className="text-slate-500">Verwachte uren:</span> {order.expectedHours ?? "-"}</p>
              <p><span className="text-slate-500">Max uren:</span> {order.maxHours ?? "-"}</p>
            </div>
          )}

          {order.type === "material_order" && (
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Orderregels</h3>
              <ProcurementOrderLinesTable lines={Array.isArray(order.lines) ? order.lines : []} />
            </div>
          )}

          {String(order.associationType || "") === "nog_te_koppelen" ||
          String(order.associationType || "") === "voorraad_magazijn" ? (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-900">Later koppelen</h3>
              <p className="mt-1 text-xs text-amber-800">
                Koppel deze inkoopbon optioneel aan een project of werkbon. Bij status
                <code className="mx-1 rounded bg-white px-1 py-0.5">nog_te_koppelen</code>
                wordt na koppelen automatisch
                <code className="mx-1 rounded bg-white px-1 py-0.5">gekoppeld</code>.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <select
                  value={linkTargetType}
                  onChange={(e) => {
                    setLinkTargetType(e.target.value);
                    setLinkTargetId("");
                  }}
                  className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="project">Koppel aan project</option>
                  <option value="workorder">Koppel aan werkbon</option>
                </select>
                <select
                  value={linkTargetId}
                  onChange={(e) => setLinkTargetId(e.target.value)}
                  className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Kies doel</option>
                  {linkTargetType === "project"
                    ? projects.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.naam || item.projectNaam || item.projectNummer || item._id}
                        </option>
                      ))
                    : workOrders.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.werkbonNummer || item.werkbonnummer || item.titel || item.title || item._id}
                        </option>
                      ))}
                </select>
                <input
                  value={linkNote}
                  onChange={(e) => setLinkNote(e.target.value)}
                  placeholder="Notitie (optioneel)"
                  className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleLinkTarget}
                disabled={linking || actionBusy}
                className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-amber-900 disabled:opacity-60"
              >
                {linking ? "Koppelen..." : "Nu koppelen"}
              </button>
            </div>
          ) : null}

          <div className="mt-4 space-y-3">
            <ProcurementOrderProcessingActions
              status={order.normalizedStatus || order.status}
              disabled={actionBusy}
              onAction={(actionKey) => runNamedAction(actionKey)}
            />
            <ProcurementOrderReviewPanel
              status={order.normalizedStatus || order.status}
              loading={actionBusy}
              onSubmit={(actionKey, note) => runNamedAction(actionKey, note)}
            />
            <ProcurementOrderReceivePanel
              type={order.type}
              status={order.normalizedStatus || order.status}
              loading={actionBusy}
              onSubmit={(actionKey, note) => runNamedAction(actionKey, note)}
            />
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <h3 className="text-sm font-semibold text-slate-900">Statushistorie</h3>
              <p className="mb-2 mt-1 text-xs text-slate-500">Placeholder voor uitgebreidere audit/approval-trace.</p>
              {Array.isArray(order.statusHistory) && order.statusHistory.length > 0 ? (
                <ul className="space-y-1 text-xs text-slate-700">
                  {order.statusHistory.slice(-8).reverse().map((entry, index) => (
                    <li key={`${entry.changedAt || "x"}-${index}`}>
                      {entry.from ? `${entry.from} -> ` : ""}{entry.to} · {formatDateTime(entry.changedAt)} · {entry.changedBy?.name || entry.changedBy?.email || "-"}{entry.note ? ` · ${entry.note}` : ""}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Nog geen statushistorie beschikbaar.</p>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
